import { Controller } from '@hotwired/stimulus';

/**
 * Lightbox - previews a card attachment in a modal <dialog> over the card: an image scaled to fit or a PDF in a frame.
 * Like the card modal, the dialog itself takes focus; Escape and a click outside the content close it (the card
 * stays open) and focus goes back to the attachment.
 * Targets: dialog - the <dialog> | body - where the image or frame goes | name - the file name | download - its download link
 * @example
 * <section data-controller="lightbox">
 *   <button data-action="lightbox#open" data-lightbox-url-param="/file" data-lightbox-kind-param="image"
 *           data-lightbox-name-param="shot.png" data-lightbox-download-param="/file?download">…</button>
 *   <dialog tabindex="-1" data-lightbox-target="dialog" data-action="click->lightbox#backdrop close->lightbox#clear">…</dialog>
 * </section>
 */
export default class extends Controller {
  static targets = ['dialog', 'body', 'name', 'download'];

  open ({ params: { url, kind, name, download } }) {
    const content = kind === 'image' ? document.createElement('img') : document.createElement('iframe');
    content.src = url;
    content.className = `cm-viewer__${kind}`;
    if (kind === 'image') content.alt = name;
    if (kind !== 'image') content.title = name;

    this.bodyTarget.replaceChildren(content);
    this.nameTarget.textContent = name;
    this.downloadTarget.href = download;
    this.dialogTarget.showModal();
    this.dialogTarget.focus({ preventScroll: true });
  }

  close () {
    this.dialogTarget.close();
  }

  // A click on the dialog itself (the dimmed area around the content) closes it.
  backdrop (event) {
    if (event.target === this.dialogTarget || event.target === this.bodyTarget) this.close();
  }

  // Closed: drop the image or frame so a PDF stops loading. The close event comes a moment later, by then
  // another file may have been opened.
  clear () {
    if (!this.dialogTarget.open) this.bodyTarget.replaceChildren();
  }
}
